import React, { useState, useEffect, useRef } from "react";
import {
  TextValidator,
  ValidatorForm,
} from "react-material-ui-form-validator";
import { InfoIconWithMessage } from "../../../shared/infoIcon/InfoIcon";
import styles from "./MerchantId.module.scss";
import "../../../styles/sharedStyles.scss";
import { withTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import { logMsg } from "../../../common/Logger";
import DealerMerchantIdClient from "../../../shared/clients/DealerMerchantIdClient";
import HasPermissionTo from "../../../common/HasPermissionTo";
import {
  IS_ZAF_MARKET,
  IS_VNM_MARKET,
  GET_DEALER_NAME,
  IS_PHL_MARKET,
} from "../../../utils/EmpUtil";
import PendingActionClient from "../../../shared/clients/PendingActionClient";
import { MERCHANT_ID } from "../../../common/Constants";
import AppActionsLogClient from "../../../shared/clients/AppActionsLogClient";

function MerchantId(props) {
  const [dealers, setDealers] = useState([]);
  const [originalDealers, setOriginalDealers] = useState([]);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const merchantIdMapRef = useRef(null);
  const merchantIdValidationRef = useRef("");

  const translationPrefix =
    IS_ZAF_MARKET(props.user.market) ||
    IS_VNM_MARKET(props.user.market)
      ? "StoreId"
      : "MerchantId";

  function isMainDealer(dealer) {
    return props.user.isMainDealer(dealer.dealerType);
  }

  function isNonOnboardedBranchDealer(dealer) {
    return !dealer.isOnboarded && props.user.isBranchDealer(dealer.dealerType);
  }

  function getMerchantIds() {
    const dealerList = [];

    const dealerMap = {};
    props.dealerGroup.forEach((dealer) => {
      dealerMap[dealer.commonId] = dealer;
    });
    props.hideOrShow(true);
    DealerMerchantIdClient.getMerchantIds(props.user)
      .then((res) => {
        const merchantIdData = res.result.dealerMerchantIdData;
        const merchantIdMap = {};
        merchantIdData.forEach((dealer) => {
          merchantIdMap[dealer.dealerCommonId] = dealer.merchantId;
          const dealerFromMap = dealerMap[dealer.dealerCommonId];
          dealerFromMap.merchantId = dealer.merchantId;
          dealerList.push(dealerFromMap);
        });
        merchantIdMapRef.current = merchantIdMap;
        dealerList.sort((a, b) => {
          if (a.dealerType === "MDLR" || a.dealerType === "HUB") {
            return -1;
          } else if (b.dealerType === "MDLR" || b.dealerType === "HUB") {
            return 1;
          } else {
            return 0;
          }
        });
        setDealers(dealerList);
        setOriginalDealers(dealerList);
      })
      .catch(() => {
      })
      .finally(() => {
        props.hideOrShow(false);
        setIsDataLoaded(true);
      });
  }

  function merchantIdChangeHandler() {
    return (event) => {
      const currentDealers = dealers;
      if (currentDealers) {
        const newDealers = currentDealers.map((dealer) => {
          if (event.target.name === dealer.commonId) {
            return { ...dealer, merchantId: event.target.value };
          }
          return dealer;
        });

        let disabled = true;

        merchantIdMapRef.current &&
        newDealers.forEach((dealer) => {
          if (disabled) {
            const merchantIdValueFromMap = merchantIdMapRef.current[dealer.commonId];
            if (merchantIdValueFromMap !== dealer.merchantId) {
              disabled = false;
            }
          }
        });

        setDealers(newDealers);
        setButtonDisabled(disabled);
      }
    };
  }

  function uploadMerchantIds(event) {
    if (event) {
      event.preventDefault();
    }

    if (props.fromQuickstart) {
      props.onSubmit({ dealers: dealers });
      return;
    }

    const updatedList = dealers;
    const originalDealersCopy = JSON.parse(JSON.stringify(originalDealers));
    const changed = [];

    for (const dealer in updatedList) {
      const commonId = updatedList[dealer].commonId;
      const copyIndex = originalDealersCopy.findIndex(obj => obj.commonId === commonId);

      if (originalDealersCopy[copyIndex].merchantId !== updatedList[dealer].merchantId) {
        changed.push({
          dealerCommonId: commonId,
          merchantId: updatedList[dealer].merchantId,
          updatedBy: props.user.userId,
        });
      }
    }

    props.hideOrShow(true);

    DealerMerchantIdClient.saveMerchantIds(changed)
      .then(() => {
        getMerchantIds();
        removePendingAction();
        if (props.user.pendingActions.length - 1 === 0) {
          props.updateDealerGroupDetails();
        }
        return Promise.resolve();
      })
      .then(() => {
        const payload = { userId: props.user.userId };
        return AppActionsLogClient.recordMerchantIdChange(props.user, payload);
      })
      .catch((err) => logMsg(err))
      .finally(() => {
        props.hideOrShow(false);
      });

    setButtonDisabled(true);
  }

  function removePendingAction() {
    const pendingActionStatusForDealers =
      props.user.getPendingActionStatusForDealers(
        props.user.pendingActions,
        MERCHANT_ID,
      );
    if (pendingActionStatusForDealers.length !== 0) {
      props.hideOrShow(true);
      PendingActionClient.removeDealerPendingAction(
        props.user,
        getDealerList(),
        MERCHANT_ID,
      )
        .then(() => {
          props.getPendingActions();
        })
        .finally(() => {
          props.hideOrShow(false);
        });
    }
  }

  function getDealerList() {
    const dealersList = [];
    let dealerList = props.user.getCommonIdsWithoutSDLR();
    if (dealerList) {
      dealerList = dealerList.split(",");
      dealerList.forEach((dealer) => {
        const formattedDealer = props.user.reformatCommonId(dealer);
        dealersList.push(
          props.user.dealerGroupingsMap[formattedDealer].completeDealerId,
        );
      });
    }
    return dealersList;
  }

  useEffect(() => {
    const STRIPE_MARKET =
      props.user.permissions.rolePermissions.hasOwnProperty(
        "stripeEnabled",
      );

    if (STRIPE_MARKET) {
      merchantIdValidationRef.current = "matchRegexp:^acct_[A-Za-z\\d]{16,25}$";
    } else if (IS_ZAF_MARKET(props.user.market)) {
      merchantIdValidationRef.current = "matchRegexp:^[0-9]{6}$";
    } else if (IS_VNM_MARKET(props.user.market)) {
      merchantIdValidationRef.current = "matchRegexp:^[A-Za-z0-9]{10,25}$";
    } else if (IS_PHL_MARKET(props.user.market)) {
      merchantIdValidationRef.current = "matchRegexp:^[A-Za-z0-9]{13,25}$";
    } else {
      merchantIdValidationRef.current = "matchRegexp:^[A-Za-z0-9]{13}$";
    }

    if (props.fromQuickstart) {
      props.showActionFooter(true);
      setDealers(
        props.dealerGroup.filter(
          (dealer) =>
            isMainDealer(dealer) || isNonOnboardedBranchDealer(dealer),
        ),
      );
      setIsDataLoaded(true);
    } else {
      getMerchantIds();
    }
  }, []);

  const subHeader = props.fromQuickstart
    ? props.t(`${translationPrefix}.subheaderFromQ`)
    : props.t(`${translationPrefix}.subheaderFromB`);

  return (
    isDataLoaded && (
      <div className="pageWrapper">
        <div className="pagePadding">
          <h1 id="merchantIdHeader" className="pageTitle">
            {props.t(`${translationPrefix}.header`)}
          </h1>
          <div className="pageInfoWrapper">
            <InfoIconWithMessage message={subHeader} />
          </div>
        </div>
        <Grid
          container
          className={`${styles.columnHeaderRow} pagePaddingLeft pagePaddingRight`}
        >
          <Grid item xs={10} className={styles.columnHeader} id="site">
            {props.t(`${translationPrefix}.site`)}
          </Grid>
          <Grid
            item
            xs={2}
            className={`${styles.columnHeader} ${styles.extraPaddingRight}`}
            id="merchantId"
          >
            {props.t(`${translationPrefix}.id`)}
          </Grid>
        </Grid>

        <ValidatorForm
          id="merchantIdForm"
          ref={props.formRef}
          onSubmit={uploadMerchantIds}
        >
          {dealers.map((dealer) => (
            <Grid
              container
              id="dealerRow"
              key={dealer.commonId}
              className={`${styles.dataRow} pagePaddingLeft pagePaddingRight`}
            >
              <Grid item xs={10} className={styles.dataRowCell}>
                {GET_DEALER_NAME(dealer, props.user.market)} (
                {dealer.dealerId})
              </Grid>
              <Grid item xs={2}>
                <HasPermissionTo
                  perform={["merchantId:write"]}
                  permissions={props.user.permissions.rolePermissions}
                  render={() => {
                    return (
                      <TextValidator
                        data-testid="inputField"
                        style={{ width: "100%" }}
                        autoComplete="off"
                        name={dealer.commonId}
                        disabled={props.isMainViewingSDLR(dealer)}
                        className={styles.merchantIdInput}
                        variant="outlined"
                        value={dealer.merchantId || ""}
                        onChange={merchantIdChangeHandler(
                          dealer.commonId,
                        )}
                        validators={["required", merchantIdValidationRef.current]}
                        errorMessages={[
                          props.t(`${translationPrefix}.errorReq`),
                          props.t(`${translationPrefix}.errorId`),
                        ]}
                      />
                    );
                  }}
                  noRender={() => {
                    return (
                      <div className={styles.merchantIdText}>
                        {dealer.merchantId}
                      </div>
                    );
                  }}
                />
              </Grid>
            </Grid>
          ))}
          <HasPermissionTo
            perform={["merchantId:write"]}
            permissions={props.user.permissions.rolePermissions}
            condition={!props.fromQuickstart}
            render={() => {
              return (
                <div
                  className={
                    buttonDisabled
                      ? "disabledButtonDiv"
                      : "confirmButton"
                  }
                >
                  <button
                    disabled={buttonDisabled}
                    data-testid="saveBtn"
                    id="saveBtn"
                    type="submit"
                  >
                    {props.t(`${translationPrefix}.saveAndUpdate`)}
                  </button>
                </div>
              );
            }}
          />
        </ValidatorForm>
      </div>
    )
  );
}

export default withTranslation("emp")(MerchantId);
